import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { icons } from '../../../assets/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import ImprimerLogo from '../../../assets/icons/svg/document-text.svg';
import './index.scss';
import { LoaderComponent } from '../../../helpers/components/loader';
import { ShopInfo, SuperAdminInfo } from '../../../components/userInfo';
import { convertDate, roundToThreeDecimals } from '../../../helpers/modules';
import PrintToPdf from '../../admin/orderDetails/PrintToPdf';
import { getOrderByIdCustomer } from '../../../api/redux/slices/Customer';

const DetailsOrderClient = () => {
	const { orderDetails } = useSelector((state) => state.customer);
	const dispatch = useDispatch();
	const params = useParams();
	const [isLoading, setIsLoading] = useState(false);
	const token = localStorage.getItem('accesstoken');
	const [countryMap, setCountryMap] = useState({});
	const [countryNames, setCountryNames] = useState({});

	const componentRef = useRef();
	const handlePrint = useReactToPrint({
		content: () => componentRef.current,
	});

	useEffect(() => {
		fetch('https://cdn.jsdelivr.net/npm/world-countries@4/dist/countries.json')
			.then((res) => res.json())
			.then((data) => {
				const map = {};
				const names = {};
				data.forEach((c) => {
					if (c.cca3 && c.cca2) {
						map[c.cca3] = c.cca2.toLowerCase();
						names[c.cca3] = c.name.common;
					}
				});
				setCountryMap(map);
				setCountryNames(names);
			})
			.catch((err) => console.error('Failed to fetch country codes:', err));
	}, []);

	const getFlagUrl = (cca3) => {
		if (!cca3 || !countryMap[cca3]) return null;
		return `https://hatscripts.github.io/circle-flags/flags/${countryMap[cca3]}.svg`;
	};

	const getCountryName = (cca3) => {
		return countryNames[cca3] || cca3;
	};

	useEffect(() => {
		setIsLoading(true);
		dispatch(getOrderByIdCustomer({ token, id: params.id }))
			.unwrap()
			.finally(() => setIsLoading(false));
	}, [dispatch, params, token]);

	// Check if shop country is Tunisia - hide contenaire if it is
	const isTunisia =
		!orderDetails?.order?.shopInfo?.country || orderDetails?.order?.shopInfo?.country === 'TUN';

	// Check if shop is NOT Tunisia - show contenaire
	const showContenaire = !isTunisia;

	const shopCountry = orderDetails?.order?.shopInfo?.country;

	// ─── Volume & contenaire data from backend ────────────────────────────────
	const totalVolume = orderDetails?.totalVolume ?? null;
	const suggestedContenaire = orderDetails?.suggestedContenaire ?? null;

	// Determine if the suggested contenaire is an exact fit, slightly larger, or oversized
	const getContenaireBadge = () => {
		if (!suggestedContenaire || totalVolume == null) return null;
		const diff = suggestedContenaire.volume - totalVolume;
		if (diff === 0) {
			return { label: 'Correspondance exacte', color: '#27ae60' };
		}
		if (diff <= 5) {
			return { label: 'Meilleure correspondance', color: '#2980b9' };
		}
		return { label: 'Plus proche disponible', color: '#e67e22' };
	};
	const badge = getContenaireBadge();

	return (
		<div id='orderDetails'>
			<div className='details-order-header'>
				<Link to={`/client/orders/${params.page}`}>
					<icons.BsArrowLeft style={{ color: '#5B5B5B', marginRight: '10px' }} />
					<h1>Commandes</h1>
				</Link>
				<div style={{ display: 'flex', gap: '10px' }}>
					<button
						style={{ display: 'flex', gap: '10px' }}
						onClick={handlePrint}
						className='button-primary'
					>
						<img src={ImprimerLogo} alt='imprimer logo' />
						Imprimer
					</button>
				</div>
			</div>

			{isLoading ? (
				<LoaderComponent />
			) : (
				<>
					<div className='container'>
						<Fragment>
							<div className='credentials'>
								<div className='left'>
									<div className='left-el'>
										<label>Numéro</label>
										<span>{orderDetails?.order?.reference ?? '-'}</span>
									</div>
									<div className='left-el'>
										<label>Date</label>
										<span>{convertDate(orderDetails?.order?.date, 'DD-MM-YYYY') ?? ''}</span>
									</div>
									<div className='left-el'>
										<label>Référence code client</label>
										<span>
											{orderDetails?.order?.clientInfo?.customs_code
												? orderDetails.order.clientInfo.customs_code
												: '-'}
										</span>
									</div>
								</div>
								<div className='left'>
									<div className='left-e'>
										<label style={{ textTransform: 'capitalize' }}>
											{orderDetails?.order?.clientInfo?.company_name ?? ''}
										</label>
									</div>
									<div className='left-e'>
										<span>{orderDetails?.order?.clientInfo?.client ?? ''}</span>
									</div>
									<div className='left-e'>
										<span>
											Matricule Fiscale:{' '}
											{orderDetails?.order?.clientInfo?.tax_registration_number
												? orderDetails.order.clientInfo.tax_registration_number
												: '-'}
										</span>
									</div>
									<div className='left-e'>
										<span>Telephone: {orderDetails?.order?.clientInfo?.phone ?? ''}</span>
									</div>
									<div className='left-e'>
										<ShopInfo shop={orderDetails?.order?.shopInfo} />
										{shopCountry && (
											<div
												style={{
													display: 'flex',
													alignItems: 'center',
													gap: '6px',
													marginTop: '4px',
												}}
											>
												{getFlagUrl(shopCountry) && (
													<img
														src={getFlagUrl(shopCountry)}
														alt={getCountryName(shopCountry)}
														width='20'
														height='20'
														style={{ borderRadius: '50%' }}
													/>
												)}
												<span
													style={{
														fontSize: '0.85rem',
														color: '#35435f',
														fontWeight: 600,
													}}
												>
													{getCountryName(shopCountry)}
												</span>
											</div>
										)}
									</div>
								</div>
							</div>

							<div className='devis'>
								<div className='devis-head'>
									<h1>Devis</h1>
									<p>
										Cher Client,
										<br />
										Nous avons bien reçu votre demande de devis et nous vous en remercions.
										<br />
										Nous vous prions de trouver ci-dessous nos conditions les meilleures
									</p>
								</div>

								<div className='devis-table'>
									<table className='offres'>
										<thead>
											<tr id='titles'>
												<th>RÉFÉRENCE</th>
												<th>RÉFÉRENCE CLT</th>
												<th>PRODUIT</th>
												<th>SOCIÉTÉ</th>
												<th>QTÉ</th>
												<th>VOLUME</th>
												<th>PU HT</th>
												<th>REMISE (%)</th>
												<th>TVA (%)</th>
												<th>MONTANT HT</th>
											</tr>
										</thead>
										<tbody>
											{orderDetails?.list?.map((el, index) => (
												<tr className='elements' key={index}>
													<td>{el?.reference ?? '-'}</td>
													<td>{el?.refranceCTL ?? '-'}</td>
													<td>{el?.designation ?? '-'}</td>
													<td>{el?.company_name ?? '-'}</td>
													<td>{el?.quantity ?? '-'}</td>
													<td>{el?.volume != null ? roundToThreeDecimals(el.volume) : '-'}</td>
													<td>{roundToThreeDecimals(el?.prise_product_HT ?? 0)} DT</td>
													<td>{el?.remise ?? 0}%</td>
													<td>{el?.tva ?? 0}%</td>
													<td>{roundToThreeDecimals(el?.montantHT ?? 0)} DT</td>
												</tr>
											))}

											{/* ── Total volume row ── */}
											{totalVolume != null && (
												<tr
													className='elements'
													style={{
														fontWeight: 700,
														borderTop: '2px solid #35435f',
														backgroundColor: '#f0f4f8',
													}}
												>
													<td colSpan={4} style={{ textAlign: 'right' }}>
														VOLUME TOTAL
													</td>
													<td>—</td>
													<td style={{ color: '#35435f' }}>
														{roundToThreeDecimals(totalVolume)} m³
													</td>
													<td colSpan={4} />
												</tr>
											)}
										</tbody>
									</table>

									{/* ── Suggested contenaire card - ONLY SHOW IF SHOP IS NOT TUNISIA ── */}
									{showContenaire && suggestedContenaire && (
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: '12px',
												margin: '16px 0',
												padding: '12px 16px',
												borderRadius: '8px',
												border: `1.5px solid ${badge?.color ?? '#ccc'}`,
												backgroundColor: '#fafafa',
												maxWidth: '420px',
											}}
										>
											<div
												style={{
													fontSize: '1.5rem',
													lineHeight: 1,
												}}
											>
												📦
											</div>
											<div>
												<div
													style={{
														fontSize: '0.75rem',
														fontWeight: 600,
														color: badge?.color ?? '#555',
														textTransform: 'uppercase',
														letterSpacing: '0.04em',
														marginBottom: '2px',
													}}
												>
													Contenaire suggéré — {badge?.label}
												</div>
												<div
													style={{
														fontSize: '1rem',
														fontWeight: 700,
														color: '#1a1a2e',
													}}
												>
													{suggestedContenaire.nom}
												</div>
												<div
													style={{
														fontSize: '0.85rem',
														color: '#555',
														marginTop: '2px',
													}}
												>
													Volume :{' '}
													<strong>{roundToThreeDecimals(suggestedContenaire.volume)} m³</strong>
													{totalVolume != null && (
														<span style={{ marginLeft: '8px', color: '#888' }}>
															(commande : {roundToThreeDecimals(totalVolume)} m³)
														</span>
													)}
												</div>
											</div>
										</div>
									)}

									{orderDetails?.companyTotals && orderDetails.companyTotals.length > 0 && (
										<div className='company-totals'>
											<h3>Totaux par société</h3>
											{orderDetails.companyTotals.map((company, idx) => (
												<div key={idx} className='company-total-item'>
													<div className='company-header'>
														<div className='company-name'>{company.company_name}</div>
														<div
															className={`company-status ${
																company.status === 'ACCEPTED' ? 'status-accepted' : 'status-order'
															}`}
														>
															Commande Status :{' '}
															{company.status === 'ACCEPTED' ? 'Accepté' : 'En cours'}
														</div>
													</div>
													<div className='company-totals-details'>
														<span>TOTAL HT: {roundToThreeDecimals(company.total_HT)} DT</span>
														<span>TVA: {roundToThreeDecimals(company.tva)} DT</span>
														{isTunisia && (
															<span>TIMBRE: {roundToThreeDecimals(company.timbre)} DT</span>
														)}
														<span className='total'>
															TOTAL: {roundToThreeDecimals(company.total)} DT
														</span>
													</div>
												</div>
											))}
										</div>
									)}

									<div className='total-devis'>
										<div id='box'>
											<div>
												<h1>TOTAL HT</h1>
												<span id='somme-totale'>
													{roundToThreeDecimals(orderDetails?.order?.total_HT ?? 0)} DT
												</span>
											</div>
											{isTunisia && (
												<div>
													<h1>TIMBRE</h1>
													<span id='somme-totale'>
														{roundToThreeDecimals(orderDetails?.order?.timbre ?? 0)} DT
													</span>
												</div>
											)}
											<div>
												<h1>TOTAL TTC</h1>
												<span id='somme-totale'>
													{roundToThreeDecimals(orderDetails?.order?.total ?? 0)} DT
												</span>
											</div>
											{/* ── Total volume summary box ── */}
											{totalVolume != null && (
												<div>
													<h1>VOLUME TOTAL</h1>
													<span id='somme-totale'>{roundToThreeDecimals(totalVolume)} m³</span>
												</div>
											)}
										</div>
									</div>
								</div>

								<div className='message'>
									<p>
										Nous sommes à votre disposition pour tout complément d'informations. <br />
										Nous vous prions d'agréer, Cher Client, nos sincères salutations. <br />
										Le service commercial.
									</p>
								</div>
								<SuperAdminInfo superAdminInfo={orderDetails?.companies} />
							</div>
						</Fragment>
					</div>

					<div style={{ display: 'none' }}>
						<PrintToPdf
							componentRef={componentRef}
							orderDetails={orderDetails}
							companies={orderDetails?.companies}
							companyTotals={orderDetails?.companyTotals}
							isAdmin={false}
						/>
					</div>
				</>
			)}
		</div>
	);
};

export default DetailsOrderClient;
